import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { SalesFunnelStage } from "@/services/dashboardService";

interface SalesFunnelBarChartProps {
  data: SalesFunnelStage[];
  currencySymbol: string;
}

export function SalesFunnelBarChart({ data, currencySymbol }: SalesFunnelBarChartProps) {
  // Filter out stages with 0 opportunities to avoid visual clutter
  const chartData = data
    .filter(stage => stage.opportunities_count > 0)
    .map(stage => ({
      name: stage.stage_name,
      count: stage.opportunities_count,
      value: stage.total_value,
      color: stage.stage_color,
      conversion: stage.conversion_rate,
    }));

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white/95 backdrop-blur-md p-4 border border-slate-200/50 rounded-xl shadow-xl shadow-slate-200/50 min-w-[200px]">
          <p className="font-bold text-slate-900 mb-2 flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: data.color }} />
            {data.name}
          </p>
          <div className="space-y-1">
            <div className="flex justify-between items-center gap-4 text-sm">
              <span className="text-slate-500">Oportunidades:</span>
              <span className="font-semibold text-slate-900">{data.count}</span>
            </div>
            <div className="flex justify-between items-center gap-4 text-sm">
              <span className="text-slate-500">Valor Total:</span>
              <span className="font-semibold text-emerald-600">{currencySymbol}{(data.value / 1000).toFixed(1)}K</span>
            </div>
            {typeof data.conversion === "number" && data.conversion > 0 && (
              <div className="flex justify-between items-center gap-4 text-sm">
                <span className="text-slate-500">Conversión:</span>
                <span className="font-semibold text-blue-600">{data.conversion.toFixed(1)}%</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[350px] text-slate-400 font-medium">
        No hay oportunidades activas en este período
      </div>
    );
  }

  return (
    <div className="w-full h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
          <XAxis 
            type="number"
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#94A3B8', fontSize: 12 }}
            allowDecimals={false}
          />
          <YAxis 
            dataKey="name"
            type="category"
            axisLine={false} 
            tickLine={false} 
            tick={{ fill: '#64748B', fontSize: 12, fontWeight: 500 }}
            width={120}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(241, 245, 249, 0.4)' }} />
          <Bar dataKey="count" radius={[0, 6, 6, 0]} maxBarSize={24} animationDuration={1000}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color || '#3B82F6'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
